import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  LinearProgress,
  Paper,
  TextField,
} from '@mui/material';
import {
  People as PeopleIcon,
  Storage as StorageIcon,
  FileCopy as FileIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchSystemStats, fetchSystemHealth } from '../store/slices/systemSlice';
import { fetchUsers } from '../store/slices/userSlice';
import { fetchEntities } from '../store/slices/entitySlice';
import { showInfo } from '../store/slices/toastSlice';
import { analyzeTopic } from '../store/slices/topicAnalysisSlice';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { stats, health, isLoading } = useAppSelector((state) => state.system);
  const { users } = useAppSelector((state) => state.users);
  const { entities } = useAppSelector((state) => state.entities);

  useEffect(() => {
    dispatch(fetchSystemStats());
    dispatch(fetchSystemHealth());
    dispatch(fetchUsers());
    dispatch(fetchEntities());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchSystemStats());
    dispatch(fetchSystemHealth());
    dispatch(showInfo({
      title: 'Refreshing',
      message: 'Dashboard data is being refreshed...',
      duration: 2000,
    }));
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  const roleDistributionData = users
    ? Object.entries(
        users.reduce((acc, user) => {
          acc[user.role] = (acc[user.role] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      ).map(([role, count]) => ({
        name: role.charAt(0).toUpperCase() + role.slice(1),
        value: count,
      }))
    : [];

  const systemHealthData = health
    ? [
        { service: 'System', status: health.status === 'healthy' ? 'Healthy' : 'Warning' },
        { service: 'Memory', status: health.memory_usage < '80%' ? 'Healthy' : 'Warning' },
        { service: 'CPU', status: health.cpu_usage < '80%' ? 'Healthy' : 'Warning' },
        { service: 'Disk', status: health.disk_usage < '80%' ? 'Healthy' : 'Warning' },
      ]
    : [];

  const entityStatusData = entities
    ? Object.entries(
        entities.reduce((acc, entity) => {
          acc[entity.status] = (acc[entity.status] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      ).map(([status, count]) => ({
        name: status.charAt(0).toUpperCase() + status.slice(1),
        value: count,
      }))
    : [];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                  <Typography variant="h4" component="h2">
                    {stats?.total_users || 0}
                  </Typography>
                </Box>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Box mt={2}>
                <Chip
                  label={`${stats?.active_users || 0} Active`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Knowledge Bases
                  </Typography>
                  <Typography variant="h4" component="h2">
                    {stats?.total_entities || 0}
                  </Typography>
                </Box>
                <StorageIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Box mt={2}>
                <Chip
                  label={`${stats?.active_entities || 0} Active`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Files
                  </Typography>
                  <Typography variant="h4" component="h2">
                    {stats?.total_files || 0}
                  </Typography>
                </Box>
                <FileIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Box mt={2}>
                <Chip
                  label="Files Uploaded"
                  color="info"
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    System Health
                  </Typography>
                  <Typography variant="h6" component="h2">
                    {health?.uptime || 'N/A'}
                  </Typography>
                </Box>
                <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Box mt={2}>
                <Chip
                  label={health?.status || 'Unknown'}
                  color={health?.status === 'healthy' ? 'success' : 'warning'}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* User Role Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Role Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={roleDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {roleDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Entity Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Knowledge Base Status
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={entityStatusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Health */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Health Overview
          </Typography>
          <Grid container spacing={2}>
            {systemHealthData.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                    {item.status === 'Healthy' ? (
                      <CheckCircleIcon color="success" />
                    ) : item.status === 'Warning' ? (
                      <WarningIcon color="warning" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {item.service}
                  </Typography>
                  <Chip
                    label={item.status}
                    color={item.status === 'Healthy' ? 'success' : 'warning'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Quick Topic Analysis */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" gutterBottom>
              Quick Topic Analysis
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/topic-analysis')}
              startIcon={<TrendingUpIcon />}
            >
              Full Analysis
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Get AI-powered insights and visualizations for any topic. Try analyzing topics like "medical", "technology", or "finance".
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              label="Enter a topic"
              size="small"
              placeholder="e.g., medical, technology..."
              sx={{ flexGrow: 1 }}
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  const topic = (e.target as HTMLInputElement).value;
                  if (topic.trim()) {
                    dispatch(analyzeTopic({ topic: topic.trim() }));
                    navigate('/admin/topic-analysis');
                  }
                }
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/admin/topic-analysis')}
            >
              Analyze
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Knowledge Bases
          </Typography>
          <Grid container spacing={2}>
            {entities?.slice(0, 4).map((entity) => (
              <Grid item xs={12} sm={6} md={3} key={entity.id}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {entity.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {entity.description?.substring(0, 50)}...
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      label={entity.status}
                      color={
                        entity.status === 'active' ? 'success' :
                        entity.status === 'processing' ? 'warning' : 'default'
                      }
                      size="small"
                    />
                    <Chip
                      label={`${entity.file_count} files`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
